import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, ReplaySubject, map, switchMap, take } from 'rxjs';

import { ConfirmDialogData, UserGoals } from '@cpd-app/core-module/models';
import { ConfirmDialogComponent } from '@cpd-app/shared-module/components/confirm-dialog/confirm-dialog.component';

import { AccountCreationPromptService } from '@cpd-app/core-module/services/auth/account-creation-prompt.service';
import { GoalFacade } from '@cpd-app/state/goal';
import { careerGoalCutsEducation } from '@cpd-app/state/goal/career-change';

/** The words the replace-career-goal confirmation uses. */
export interface ReplaceCareerGoalWording {
  title: string;
  message: string;
  confirmText: string;
}

/**
 * A11Y.1 (28 Sep 508 rescan, WCAG 3.3.4). WHAT REPLACING A CAREER GOAL SAYS.
 *
 * Set Career Goal on a different occupation replaced the career goal without
 * a word, and `career-change.ts` clears the program, school, credential and
 * license with it. That is the same loss a Remove asks about, so it asks the
 * same way: it names the goal being replaced and every goal that goes with
 * it, and only the ones actually set, in the removal's own wording.
 */
export function replaceCareerGoalWording(
  current: UserGoals | null | undefined,
  next: { onetCode: string; title: string }
): ReplaceCareerGoalWording {
  const currentTitle = current?.occupationTitle || 'your current career goal';
  const cuts = careerGoalCutsEducation(current?.onetCode, next.onetCode);
  const going = cuts
    ? [
        current?.schoolProgramId || current?.schoolProgramTitle ? 'program' : '',
        current?.schoolId || current?.schoolTitle ? 'school' : '',
        current?.certificationId || current?.credentialCtid ? 'credential' : '',
        current?.licenseId ? 'license' : ''
      ].filter((noun) => !!noun)
    : [];
  const list =
    going.length > 1
      ? `${going.slice(0, -1).join(', ')} and ${going[going.length - 1]}`
      : going[0];
  const also = list
    ? ` It will also remove your ${list} ${going.length > 1 ? 'goals' : 'goal'}.`
    : '';
  return {
    title: 'You are about to replace your career goal',
    message: `Setting ${next.title} as your career goal will replace ${currentTitle}.${also} This action cannot be undone.`,
    confirmText: 'Replace Goal'
  };
}

/**
 * CPD-679. What removing the program says: the school goes with it, by name,
 * when one is set.
 */
export function programRemovalWording(
  goals: UserGoals | null | undefined,
  schoolId: number | null = null
): { message: string; confirmText: string } {
  const hasSchool = schoolId != null || !!goals?.schoolId || !!goals?.schoolTitle;
  if (!hasSchool) {
    return {
      message: 'Your career goal stays. This action cannot be undone.',
      confirmText: 'Remove Goal'
    };
  }
  const school = goals?.schoolTitle ? `school goal, ${goals.schoolTitle},` : 'school goal';
  return {
    message: `Your ${school} will be removed too, because it was chosen for this program. Your career goal stays. This action cannot be undone.`,
    confirmText: 'Remove Goals'
  };
}

/**
 * Removing a goal from the report.
 *
 * The five methods lived on `GoalsReportSummaryComponent`, whose goals block
 * was de-duplicated against the masthead. The masthead is on two pages now (the
 * plan page and the classic report), so they live here rather than being
 * written twice.
 *
 * Every removal goes through the account-creation prompt first: an anonymous
 * visitor is asked to sign in before a goal is dropped. A signed-in member then
 * confirms it. They never used to: the prompt passes an authenticated member
 * straight through, so one press of Remove deleted the goal, and removing the
 * career goal takes the program, school, credential and license with it (WCAG
 * 3.3.4, found in the 508 test run). The wording is the goal dialog's own.
 *
 * Each removal returns whether it went ahead, once the confirmation has
 * closed, so the caller can put focus somewhere that still exists. The
 * masthead's goal dialog and the schools step route through here too: they
 * called the facade directly, so their Remove deleted without asking.
 */
@Injectable({ providedIn: 'root' })
export class GoalRemovalService {
  private readonly goalFacade = inject(GoalFacade);
  private readonly accountCreationPromptService = inject(
    AccountCreationPromptService
  );
  private readonly dialog = inject(MatDialog);

  /**
   * ONE WRITE, AND THE CASCADE HAPPENS IN IT.
   *
   * CPD-668. This dispatched three removals in a row -- school, program,
   * career -- and each became its own mutation. `updateGoals$` builds every
   * write from the goals in the store, which only update when a write
   * SUCCEEDS, so all three were built from the same stale snapshot: the first
   * two re-sent the career goal the member was removing, and the third
   * re-sent the school and program it was meant to have cleared. Whichever
   * reached the server last decided what survived.
   *
   * Clearing the career goal now clears program, school, credential and
   * license inside that single write (CPD-661 item 8; `career-change.ts`
   * holds the rule), so there is nothing left to sequence here.
   */
  public removeCareerGoal(): Observable<boolean> {
    return this.confirm(
      () => this.goalFacade.setCareerGoal(null, null, null),
      'You are about to remove all goals',
      'Removing your career goal will also remove your program, school, credential and license goals. This action cannot be undone.',
      'Remove Goals'
    );
  }

  /** CPD-661 item 9: only the career goal cascades. */
  public removeSchoolGoal(): Observable<boolean> {
    return this.confirm(
      () => this.goalFacade.setInstitutionGoal(null),
      'You are about to remove your school goal',
      'Your program goal will stay. This action cannot be undone.',
      'Remove Goal'
    );
  }

  /**
   * The program, and the school chosen for it (CPD-679, Phong 29 Sep 2026).
   *
   * The school is picked from a list filtered to the program, so a school
   * left behind could be one that does not teach whatever program comes next.
   * The effect clears it (setInstitutionProgramGoal$); this says so first,
   * naming the school, and only when one is set.
   */
  public removeAcademicProgramGoal(schoolId: number | null): Observable<boolean> {
    let held: UserGoals | null | undefined = null;
    this.goalFacade.goals$.pipe(take(1)).subscribe((goals) => (held = goals));
    const { message, confirmText } = programRemovalWording(held, schoolId);

    return this.confirm(
      () => this.goalFacade.setInstitutionProgramGoal(null, null, null),
      'You are about to remove your program goal',
      message,
      confirmText
    );
  }

  public removeCertificationGoal(): Observable<boolean> {
    return this.confirm(
      () => this.goalFacade.setCertification(null),
      'You are about to remove your credential goal',
      'Your career goal stays. This action cannot be undone.',
      'Remove Goal'
    );
  }

  /**
   * The Registry credential. Clearing it clears the legacy certification id
   * with it (see the effect), because the goal dialog's credential row shows
   * whichever of the two is set.
   */
  public removeCredentialGoal(): Observable<boolean> {
    return this.confirm(
      () => this.goalFacade.setCredentialGoal(null, null),
      'You are about to remove your credential goal',
      'Your career goal stays. This action cannot be undone.',
      'Remove Goal'
    );
  }

  public removeLicensureGoal(): Observable<boolean> {
    return this.confirm(
      () => this.goalFacade.setLicense(null),
      'You are about to remove your license goal',
      'Your career goal stays. This action cannot be undone.',
      'Remove Goal'
    );
  }

  /**
   * A11Y.1, 3.3.4. Asks before a career goal is REPLACED; writes nothing.
   *
   * The caller does the write, exactly as before, once this says yes: the
   * write and its cascade are unchanged. Cancel writes nothing, and Material
   * puts focus back on the Set Career Goal button that opened the question.
   * No sign-in prompt, because setting a goal never had one.
   */
  public confirmReplaceCareerGoal(
    current: UserGoals | null | undefined,
    next: { onetCode: string; title: string }
  ): Observable<boolean> {
    const { title, message, confirmText } = replaceCareerGoalWording(current, next);
    const outcome = new ReplaySubject<boolean>(1);
    let confirmed = false;
    this.dialog
      .open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
        data: {
          title,
          message,
          confirmText,
          cancelText: 'Cancel',
          icon: 'warning',
          confirmFn: () => {
            confirmed = true;
          }
        }
      })
      .afterClosed()
      .pipe(map(() => confirmed))
      .subscribe(outcome);
    return outcome.asObservable();
  }

  private confirm(
    remove: () => void,
    title: string,
    message: string,
    confirmText: string
  ): Observable<boolean> {
    // Replayed, so a caller that subscribes late still hears the answer.
    const outcome = new ReplaySubject<boolean>(1);
    this.accountCreationPromptService
      .openAccountCreationDialogWithConfirm('goals-report')
      .pipe(
        take(1),
        switchMap(() => {
          let removed = false;
          return this.dialog
            .open<ConfirmDialogComponent, ConfirmDialogData>(
              ConfirmDialogComponent,
              {
                data: {
                  title,
                  message,
                  confirmText,
                  cancelText: 'Cancel',
                  icon: 'warning',
                  confirmFn: () => {
                    removed = true;
                    remove();
                  }
                }
              }
            )
            .afterClosed()
            .pipe(map(() => removed));
        })
      )
      .subscribe(outcome);
    return outcome.asObservable();
  }
}
